import {useMutation, useQueryClient} from '@github-ui/react-query'
import {acquireRelay, dispatchRelayTurn, releaseRelay, whenRelayConnected} from '@github-ui/ahp-relay/relay-registry'
import {connectAndCreateSession} from '@github-ui/ahp-relay/mission-control/mission-control-environment-client'
import type {MCConnectWithSessionResponse} from '@github-ui/ahp-relay/mission-control/mission-control-environment-client'

import {fetchTask} from './use-task'
import type {OptimisticTaskListConfig} from './use-create-local-device-session'
import type {HydratedAPITask} from '../types/api-task'
import type {OptimisticTask} from '../types/task'
import {RelaySessionEvents} from '../ahp/relay-session-events'
import {mcFetcher} from '../utils/mc-fetcher'
import {fetchCAPI} from '../utils/fetch-capi'
import {sortTasksByDate, transformAPITaskToUnhydrated} from '../utils/task-helpers'
import {uuid} from '../utils/uuid'

const PRECONNECT_RELEASE_AFTER_MS = 30_000

export interface CreateEnvironmentTaskInput {
  environmentId: string
  problemStatement: string
}

export interface CreateEnvironmentTaskResult {
  task: HydratedAPITask
  sessionId: string
  environmentId: string
}

export interface UseCreateEnvironmentTaskOptions<TQueryData = unknown> {
  optimisticTaskListConfig?: OptimisticTaskListConfig<TQueryData>
}

interface MutationContext {
  clientGeneratedId: string
}

/**
 * Creates an AHP-environment-backed task in Mission Control and returns its
 * full hydrated representation.
 *
 * Three things happen in one mutation, in order:
 *
 * 1. `GET /agents/environments/:envId/connect?create_session=true` — MC mints
 *    WPS credentials AND pre-creates a Session bound to the environment.
 * 2. The pre-obtained `MCConnectResponse` is handed straight to the AHP
 *    relay registry via {@link acquireRelay}'s `preconnect` option, so the
 *    relay opens its WebSocket against the original `client_id`'s WPS groups.
 *    A second `/connect` would mint a new client_id with different groups and
 *    miss every event the daemon publishes to the first lane.
 * 3. The full {@link HydratedAPITask} is fetched so the caller can navigate
 *    to the normal task page (`generateTaskPath…`) and update its task list
 *    cache.
 *
 * The hook keeps the registry ref for {@link PRECONNECT_RELEASE_AFTER_MS} so
 * the task page's own `useAhpRelay` can acquire (bumping refcount to 2)
 * before this ref is released; the registry's dispose grace bridges any
 * remaining gap if navigation stalls.
 */
export function useCreateEnvironmentTask<TQueryData = unknown>(options?: UseCreateEnvironmentTaskOptions<TQueryData>) {
  const queryClient = useQueryClient()
  const config = options?.optimisticTaskListConfig

  return useMutation<CreateEnvironmentTaskResult, Error, CreateEnvironmentTaskInput, MutationContext>({
    onMutate: async (input): Promise<MutationContext> => {
      const clientGeneratedId = uuid()
      if (!config) return {clientGeneratedId}

      await queryClient.cancelQueries({queryKey: config.queryKey})
      const optimistic = createOptimisticEnvironmentTask({
        clientGeneratedId,
        problemStatement: input.problemStatement,
      })
      queryClient.setQueryData<TQueryData>(config.queryKey, oldData =>
        config.setFlatTaskList(oldData, [optimistic, ...(config.getFlatTaskList(oldData) ?? [])]),
      )
      return {clientGeneratedId}
    },
    mutationFn: async ({environmentId, problemStatement}): Promise<CreateEnvironmentTaskResult> => {
      const connectResp: MCConnectWithSessionResponse = await connectAndCreateSession(environmentId, mcFetcher)

      const sessionId = connectResp.session_id
      if (!sessionId) {
        throw new Error('Mission Control did not create a session for this environment')
      }

      // Until MC exposes `task_id` on ClientTokenResponse, derive it from the
      // session row. Reading Session is enough — `task_id` is populated
      // synchronously by MC's OnSessionCreate before /connect returns.
      const taskId = connectResp.task_id ?? (await fetchTaskIdFromSession(sessionId))
      if (!taskId) {
        throw new Error('Mission Control session is missing a task id')
      }

      // Seed the relay registry with the credentials we already hold so the
      // task page's own useAhpRelay reuses the existing entry instead of
      // issuing a second /connect.
      acquireRelay({
        taskId,
        environmentId,
        sessionId,
        fetcher: mcFetcher,
        createSink: id => new RelaySessionEvents(id, queryClient),
        preconnect: connectResp,
      })

      // Send the user's problem statement as the first turn over the relay
      // once it is connected. MC's `start_session` envelope only creates the
      // session shell; the daemon does not derive a userMessage from it, so
      // without this dispatch the agent sits at `turns: []` indefinitely.
      // Failures here must not abort the task — the page will still load and
      // the user can retype from the composer.
      try {
        await whenRelayConnected(taskId)
        dispatchRelayTurn(taskId, problemStatement)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to dispatch initial AHP turn for environment task', err)
      }

      // Release this ref after a window long enough for the task page to
      // mount and acquire its own ref; the registry's dispose grace handles
      // the gap if navigation is slow.
      setTimeout(() => releaseRelay(taskId), PRECONNECT_RELEASE_AFTER_MS)

      const task = await fetchTask({taskId})
      return {task, sessionId, environmentId}
    },
    onSuccess: ({task}, _input, context) => {
      if (!config || !context) return
      const transformed = transformAPITaskToUnhydrated(task)
      queryClient.setQueryData<TQueryData>(config.queryKey, oldData => {
        const cached = config.getFlatTaskList(oldData) ?? []
        const withoutOptimistic = cached.filter(t => t.clientGeneratedId !== context.clientGeneratedId)
        const next = transformed ? sortTasksByDate([transformed, ...withoutOptimistic]) : withoutOptimistic
        return config.setFlatTaskList(oldData, next.slice(0, config.pageSize))
      })
      queryClient.invalidateQueries({queryKey: config.queryKey, exact: true})
    },
    onError: (_error, _input, context) => {
      if (!config || !context) return
      queryClient.setQueryData<TQueryData>(config.queryKey, oldData => {
        const cached = config.getFlatTaskList(oldData) ?? []
        return config.setFlatTaskList(
          oldData,
          cached.filter(t => t.clientGeneratedId !== context.clientGeneratedId),
        )
      })
    },
  })
}

interface SessionFetchShape {
  task_id?: string
  session?: {task_id?: string}
}

async function fetchTaskIdFromSession(sessionId: string): Promise<string | undefined> {
  const response = await fetchCAPI({path: `/agents/sessions/${encodeURIComponent(sessionId)}`})
  const body = (await response.json()) as SessionFetchShape
  return body.task_id ?? body.session?.task_id
}

interface CreateOptimisticEnvironmentTaskParams {
  clientGeneratedId: string
  problemStatement: string
}

function createOptimisticEnvironmentTask({
  clientGeneratedId,
  problemStatement,
}: CreateOptimisticEnvironmentTaskParams): OptimisticTask {
  return {
    resource_type: 'pull',
    resource_id: -1,
    resource_global_id: '',
    created_at: new Date().toISOString(),
    session_count: 1,
    latest_session_updated_at: new Date().toISOString(),
    latest_session_state: 'in_progress',
    isOptimistic: true,
    clientGeneratedId,
    problemStatement,
    name: problemStatement,
  }
}
